<template>
  <div class="layout-wrapper">
    <HeaderTemplate v-if="!isLoginPage"/>
    <SidebarTemplate v-if="!isLoginPage"/>

    <div class="layout-content">
      <router-view></router-view>
    </div>

    <FooterTemplate v-if="!isLoginPage"/>

    <!-- WIDGET LOTTIE -->
    <Transition name="slide-fade-appvue">
      <div
        v-if="!isLoginPage"
        v-show="isLottieVisible"
        class="lottie-widget-appvue"
      >
        <Vue3Lottie
          ref="lottieRef"
          :animationLink="currentAnimationPath"
          :height="250"
          :width="250"
          :loop="true"          
          :autoPlay="true"     
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import { watch } from 'vue'; 
import { useRoute } from 'vue-router';
import { Vue3Lottie } from 'vue3-lottie'; 
import { useSystem } from '@/composables/useSystem.js';

import HeaderTemplate from '@/components/partial/HeaderPartial.vue';
import FooterTemplate from '@/components/partial/FooterPartial.vue';
import SidebarTemplate from '@/components/partial/SidebarPartial.vue';

export default {
  data() {
    return {
      isLoginPage: false,
      bootstrapReady: false,

      // --- CONFIG LOTTIE ---
      isLottieVisible: false,
      animationTimer: null,      // timeout antar kemunculan
      lottieFiles: [
        'Animation - hacker.json',
        'Hacker.json',
        'Happy Dog.json',
        'Orange skating.json',
        'Skuls Warrior.json'
      ],
      currentIndex: 0,
      activityHandler: null
    };
  },
  computed: {
    currentAnimationPath() {
      return `/assets/animation/${this.lottieFiles[this.currentIndex]}`;
    }
  },
  components: {
    HeaderTemplate,
    FooterTemplate,
    SidebarTemplate,
    Vue3Lottie 
  },
  setup() {
    const route = useRoute();
    const { fetchSystemIdentity, updateFavicon, faviconUrl } = useSystem();

    // Watch for favicon changes and update browser link
    watch(faviconUrl, () => {
      updateFavicon();
    });

    return { 
      route,
      fetchSystemIdentity,
      updateFavicon
    };
  },
  mounted() {
    this.loadBootstrap();
    this.checkLoginPage(); 
    // Fetch identity and update favicon
    this.fetchSystemIdentity().then(() => {
      this.updateFavicon();
    });
    // mulai jadwal pertama (10 detik kosong dulu)
    this.scheduleNextAnimation();
  },
  beforeUnmount() {
    if (this.animationTimer) clearTimeout(this.animationTimer);
    this.removeActivityListener();
  },
  watch: {
    '$route'(to) {
      this.checkLoginPage();
    }
  },
  methods: {
    checkLoginPage() {
      // Hide sidebar for both login and register pages
      this.isLoginPage = this.$route.path === '/login' || this.$route.path === '/register';
    },

    // ====== POLA WAKTU BARU ======
    // 1) scheduleNextAnimation: tunggu 10 detik dalam keadaan TIDAK TAMPIL
    // 2) triggerAnimation: tampilkan + play + pasang listener aktivitas
    // 3) saat user gerak -> hideAnimation: sembunyi & ganti index, lalu balik ke (1)

    scheduleNextAnimation() {
      if (this.animationTimer) clearTimeout(this.animationTimer);

      this.animationTimer = setTimeout(() => {
        this.triggerAnimation();
      }, 10000); // jeda 10 detik setelah animasi hilang
    },

    triggerAnimation() {
      // JANGAN ganti currentIndex di sini, biar tidak terlihat “kedip” di tengah
      this.isLottieVisible = true;

      this.$nextTick(() => {
        if (this.$refs.lottieRef) {
          this.$refs.lottieRef.stop();
          this.$refs.lottieRef.play();
        }
      });

      this.addActivityListener();
    },

    hideAnimation() {
      // 1. sembunyikan dulu animasi lama
      this.isLottieVisible = false;
      this.removeActivityListener();

      // 2. siapkan index berikutnya SAAT SUDAH TIDAK TAMPIL
      this.currentIndex = (this.currentIndex + 1) % this.lottieFiles.length;

      // 3. jadwalkan kemunculan berikutnya (10 detik lagi, kosong dulu)
      this.scheduleNextAnimation();
    },

    addActivityListener() {
      this.removeActivityListener();
      this.activityHandler = () => {
        this.hideAnimation();
      };
      window.addEventListener('mousemove', this.activityHandler);
      window.addEventListener('click', this.activityHandler);
      window.addEventListener('keydown', this.activityHandler);
      window.addEventListener('scroll', this.activityHandler);
    },

    removeActivityListener() {
      if (!this.activityHandler) return;
      window.removeEventListener('mousemove', this.activityHandler);
      window.removeEventListener('click', this.activityHandler);
      window.removeEventListener('keydown', this.activityHandler);
      window.removeEventListener('scroll', this.activityHandler);
      this.activityHandler = null;
    },

    // ====== BOOTSTRAP ASLI ======
    loadBootstrap() {
      if (window.bootstrap) {
        this.bootstrapReady = true;
        this.emitBootstrapReady();
        return;
      }
      console.log('🚀 Loading Bootstrap...');
      let bootstrap = document.createElement('script');
      bootstrap.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js';
      bootstrap.crossOrigin = 'anonymous';
      bootstrap.onload = () => {
        console.log('✅ Bootstrap loaded successfully');
        this.bootstrapReady = true;
        this.emitBootstrapReady();
      };
      bootstrap.onerror = () => {
        console.error('❌ Failed to load Bootstrap');
      };
      document.head.appendChild(bootstrap);
    },
    
    emitBootstrapReady() {
      this.$nextTick(() => {
        window.dispatchEvent(new CustomEvent('bootstrap-ready'));
      });
    }
  }
};
</script>

<style>
@import '../public/assets/vendor/bootstrap/css/bootstrap.min.css';
@import '../public/assets/css/theme-variables.css';
@import '../public/assets/css/style.css';
@import '../public/assets/vendor/bootstrap-icons/bootstrap-icons.css';
@import '../node_modules/nprogress/nprogress.css';
@import '../public/assets/css/user-management.css';
@import '../public/assets/css/login.css';
@import '../public/assets/css/system-identity.css';
@import '../public/assets/css/profile-theme.css';
@import '../public/assets/css/sidebar-theme.css';
@import '../public/assets/css/header-theme.css';
@import '../public/assets/css/instances-theme.css';
@import '../public/assets/css/warming-theme.css';
@import '../public/assets/css/worker-theme.css';

@import 'https://fonts.gstatic.com" rel="preconnect';
@import 'https://fonts.googleapis.com/css?family=Open-Sans:300,300i,400,400i,600,600i,700,700i|Nunito:300,300i,400,400i,600,600i,700,700i|Poppins:300,300i,400,400i,500,500i,600,600i,700,700i" rel="stylesheet"';

nav {
  padding: 0px;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

.layout-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* --- CSS TAMBAHAN --- */

.lottie-widget-appvue {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 99999;
  pointer-events: none;
}

.slide-fade-appvue-enter-active,
.slide-fade-appvue-leave-active {
  transition: all 0.8s cubic-bezier(0.25, 1, 0.5, 1);
}

.slide-fade-appvue-enter-from,
.slide-fade-appvue-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
